// src/components/fees/PaymentsTransactions.js
//
// Payments & Transactions: one list over manual collections and SchoolPay
// payments (GET /fees/transactions). Recording stays on Fee Payments and
// SchoolPay sync/adhoc stay on their own pages; this page views, filters,
// exports, and resolves unposted SchoolPay money via the existing match API.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputAdornment,
  InputLabel, MenuItem, Paper, Select, Skeleton, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  SyncAlt as SyncAltIcon,
} from '@mui/icons-material';
import { academicAPI, feeAPI, schoolPayAPI, studentAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { formatCurrency } from '../../utils/formatters';

const EMPTY_FILTERS = {
  search: '', source: '', status: '', payment_method: '', term_id: '', year: '', from_date: '', to_date: '',
};

const STATUS_META = {
  posted: { label: 'Posted', color: 'success', hint: 'In the fee ledger and counted as collected.' },
  pending: { label: 'Pending', color: 'warning', hint: 'Confirmed by SchoolPay, awaiting matching or approval. Not yet collected.' },
  unmatched: { label: 'Unmatched', color: 'error', hint: 'Received by SchoolPay but not matched to a student or fee group. Not yet collected.' },
  not_completed: { label: 'Not completed', color: 'default', hint: 'SchoolPay did not report this payment as completed. It will not be credited.' },
  awaiting_payment: { label: 'Awaiting payment', color: 'info', hint: 'Payment request sent; no payment confirmed by SchoolPay yet.' },
  failed: { label: 'Failed', color: 'error', hint: 'SchoolPay reported the payment request as failed.' },
  expired: { label: 'Expired', color: 'default', hint: 'The payment request expired without payment.' },
};

const CHANNEL_LABELS = {
  manual: 'Recorded by staff',
  schoolpay_webhook: 'SchoolPay notification',
  schoolpay_sync: 'SchoolPay sync',
  schoolpay_adhoc: 'SchoolPay payment request',
  schoolpay: 'SchoolPay',
};

const METHOD_LABELS = { cash: 'Cash', mobile_money: 'Mobile Money', bank: 'Bank' };

const MATCHABLE_STATUSES = ['pending', 'unmatched'];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: 'default' };
  return <Chip label={meta.label} color={meta.color} size="small" variant={status === 'posted' ? 'filled' : 'outlined'} />;
};

const SourceChip = ({ source }) => (
  <Chip
    label={source === 'schoolpay' ? 'SchoolPay' : 'Manual'}
    size="small"
    variant="outlined"
    color={source === 'schoolpay' ? 'secondary' : 'primary'}
  />
);

const studentLabel = (row) => row.student?.name || row.provider_student_name || 'Unknown student';

const referenceLabel = (row) => (row.fee_payment_id ? `#${row.fee_payment_id}` : '—');

const SummaryCard = ({ label, value, caption, color = 'text.primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ pb: '16px !important' }}>
      <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>{label}</Typography>
      <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
      {caption ? <Typography variant="caption" color="text.secondary">{caption}</Typography> : null}
    </CardContent>
  </Card>
);

const DetailRow = ({ label, children }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{children ?? '-'}</Typography>
  </Grid>
);

const PaymentsTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.FEES.PAYMENTS, ACTIONS.READ);
  const canCreate = can(RESOURCES.FEES.PAYMENTS, ACTIONS.CREATE);
  const canExport = can(RESOURCES.FEES.PAYMENTS, ACTIONS.EXPORT);
  const canViewStudents = can(RESOURCES.STUDENTS, ACTIONS.READ);

  const [view, setView] = useState('all');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [terms, setTerms] = useState([]);

  const [selected, setSelected] = useState(null);
  const [matchStudent, setMatchStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [matchError, setMatchError] = useState('');
  const [notice, setNotice] = useState(null);

  const requestSeq = useRef(0);

  const effectiveFilters = useMemo(
    () => (view === 'attention' ? { ...filters, status: 'needs_attention' } : filters),
    [filters, view]
  );

  const years = useMemo(
    () => [...new Set(terms.map((t) => t.year).filter(Boolean))].sort((a, b) => b - a),
    [terms]
  );

  const fetchTransactions = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(
        Object.entries({ ...effectiveFilters, page, per_page: perPage }).filter(([, v]) => v !== '' && v !== null)
      );
      const { data } = await feeAPI.getTransactions(params);
      if (seq !== requestSeq.current) return; // a newer filter change won
      setRows(Array.isArray(data?.data) ? data.data : []);
      setPagination({
        current_page: data?.current_page || 1,
        last_page: data?.last_page || 1,
        per_page: data?.per_page || perPage,
        total: data?.total || 0,
      });
      setSummary(data?.summary || null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setRows([]);
      setPagination(null);
      setSummary(null);
      setError(
        err.response?.status === 403
          ? 'You do not have permission to view payment transactions.'
          : err.response?.data?.message || 'Failed to load transactions. Please try again.'
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [effectiveFilters, page, perPage]);

  useEffect(() => {
    if (canRead) fetchTransactions();
  }, [canRead, fetchTransactions]);

  useEffect(() => {
    if (!canRead) return;
    academicAPI.getTerms()
      .then((res) => setTerms(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(() => setTerms([]));
  }, [canRead]);

  // Debounce free-text search so each keystroke isn't a request.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput.trim() ? prev : { ...prev, search: searchInput.trim() }));
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(Object.entries(effectiveFilters).filter(([, v]) => v !== ''));
      const response = await feeAPI.exportTransactions(params);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.status === 403 ? 'You do not have permission to export payments.' : 'Export failed. Please try again.' });
    } finally {
      setExporting(false);
    }
  };

  const closeDetails = () => {
    setSelected(null);
    setMatchStudent(null);
    setStudentOptions([]);
    setStudentSearch('');
    setMatchError('');
  };

  const searchStudents = async (term) => {
    setStudentSearch(term);
    if (!term || term.length < 2) { setStudentOptions([]); return; }
    setStudentSearchLoading(true);
    try {
      const response = await studentAPI.search({ search: term, per_page: 10 });
      setStudentOptions(Array.isArray(response.data) ? response.data : response.data?.data || []);
    } catch {
      setStudentOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const handleMatch = async () => {
    if (!selected || !matchStudent || matching) return;
    setMatching(true);
    setMatchError('');
    try {
      const { data } = await schoolPayAPI.manualMatch({ transaction_id: selected.record_id, student_id: matchStudent.id });
      setNotice({ type: 'success', text: data?.message || 'Payment credited to the student.' });
      closeDetails();
      fetchTransactions();
    } catch (err) {
      setMatchError(
        err.response?.status === 403
          ? 'You do not have permission to match SchoolPay payments.'
          : err.response?.data?.message || 'Could not credit this payment.'
      );
    } finally {
      setMatching(false);
    }
  };

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view payment transactions.</Alert>;
  }

  const headerActions = [
    canExport && (
      <Button key="export" variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={handleExport} disabled={exporting || loading} fullWidth={isMobile}>
        {exporting ? 'Exporting...' : 'Export CSV'}
      </Button>
    ),
    <Button key="schoolpay" variant="outlined" startIcon={<SyncAltIcon />} onClick={() => navigate('/schoolpay/transactions')} fullWidth={isMobile}>
      SchoolPay Sync
    </Button>,
    canCreate && (
      <Button key="record" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/fees/payments')} fullWidth={isMobile}>
        Record Payment
      </Button>
    ),
  ].filter(Boolean);

  const attentionCount = summary
    ? ['pending', 'unmatched', 'not_completed'].reduce((n, s) => n + (summary.by_status?.[s]?.count || 0), 0)
    : 0;

  const renderMobileItem = (row) => (
    <Box onClick={() => setSelected(row)} sx={{ cursor: 'pointer' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap>{studentLabel(row)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.student?.registration_no || row.provider_payment_code || '—'} · {formatDate(row.payment_date)}
          </Typography>
        </Box>
        <Typography variant="subtitle2" fontWeight={700} color={row.status === 'posted' ? 'success.main' : 'text.secondary'} whiteSpace="nowrap">
          {formatCurrency(row.amount)}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
        <SourceChip source={row.source} />
        <StatusChip status={row.status} />
        {row.external_reference ? <Chip size="small" label={row.external_reference} sx={{ fontFamily: 'monospace' }} /> : null}
      </Stack>
    </Box>
  );

  return (
    <Box>
      <ModulePageHeader
        title="Payments & Transactions"
        subtitle="Manual and SchoolPay payments in one place"
        actions={headerActions.length ? headerActions : null}
      />

      {notice && (
        <Alert severity={notice.type} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>
      )}

      {/* Summary: only posted rows count as collected */}
      <Grid container spacing={2} mb={3}>
        {summary ? (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard label="Collected (posted)" value={formatCurrency(summary.posted_total)} caption={`${summary.posted_count} payments`} color="success.main" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard label="Manual" value={formatCurrency(summary.manual_total)} caption={`${summary.manual_count} payments`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard label="SchoolPay" value={formatCurrency(summary.schoolpay_total)} caption={`${summary.schoolpay_count} payments`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Received, not posted"
                value={formatCurrency(summary.unposted_received_total)}
                caption={`${summary.unposted_received_count} SchoolPay payments need attention`}
                color={summary.unposted_received_count > 0 ? 'warning.main' : 'text.primary'}
              />
            </Grid>
          </>
        ) : (
          [0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 1 }} />
            </Grid>
          ))
        )}
      </Grid>

      <Tabs
        value={view}
        onChange={(e, value) => { setView(value); setPage(1); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab value="all" label="All transactions" />
        <Tab value="attention" label={attentionCount ? `Needs attention (${attentionCount})` : 'Needs attention'} />
      </Tabs>

      {view === 'attention' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          SchoolPay payments that are not in the fee ledger yet. Pending and unmatched payments can be credited to a
          student here; payments SchoolPay did not complete cannot be credited. Received does not mean settled — confirm
          settlement against the bank statement.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Student, admission no., payment ID or SchoolPay ref"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select label="Source" value={filters.source} onChange={(e) => updateFilter('source', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="schoolpay">SchoolPay</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small" disabled={view === 'attention'}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={view === 'attention' ? '' : filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(STATUS_META).map(([value, meta]) => (
                    <MenuItem key={value} value={value}>{meta.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select label="Method" value={filters.payment_method} onChange={(e) => updateFilter('payment_method', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(METHOD_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Academic year</InputLabel>
                <Select label="Academic year" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Term</InputLabel>
                <Select label="Term" value={filters.term_id} onChange={(e) => updateFilter('term_id', e.target.value)}>
                  <MenuItem value="">All terms</MenuItem>
                  {terms
                    .filter((t) => !filters.year || String(t.year) === String(filters.year))
                    .map((t) => <MenuItem key={t.id} value={t.id}>{t.name} {t.year}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="From" value={filters.from_date}
                onChange={(e) => updateFilter('from_date', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="To" value={filters.to_date}
                onChange={(e) => updateFilter('to_date', e.target.value)} InputLabelProps={{ shrink: true }}
                inputProps={{ min: filters.from_date || undefined }} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'space-between', md: 'flex-end' }}>
                {hasActiveFilters && (
                  <Button size="small" variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={clearFilters}>Clear</Button>
                )}
                <Typography variant="body2" color="text.secondary">
                  Total: <Typography component="span" color="primary" fontWeight="bold">{pagination?.total ?? 0}</Typography>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent>
          {error ? (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchTransactions}>Retry</Button>}>{error}</Alert>
          ) : loading && rows.length === 0 ? (
            [...Array(6)].map((_, i) => <Skeleton key={i} variant="text" height={44} />)
          ) : rows.length === 0 ? (
            <EmptyRecordsState
              message={
                hasActiveFilters
                  ? 'No transactions match the selected filters.'
                  : view === 'attention'
                    ? 'Nothing needs attention — every SchoolPay payment received has been posted.'
                    : 'No payments have been recorded yet.'
              }
            />
          ) : isMobile ? (
            <Box sx={{ opacity: loading ? 0.6 : 1 }}>
              <ResponsiveRecordList items={rows.map((r) => ({ ...r, id: r.uid }))} renderItem={renderMobileItem} />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ opacity: loading ? 0.6 : 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payment ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Method / Channel</TableCell>
                    <TableCell>SchoolPay Ref</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recorded by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.uid} hover onClick={() => setSelected(row)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{referenceLabel(row)}</TableCell>
                      <TableCell>{formatDate(row.payment_date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{studentLabel(row)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.student?.registration_no || (row.provider_payment_code ? `Code ${row.provider_payment_code}` : 'Not matched')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={row.status === 'posted' ? 'success.main' : 'text.secondary'}>
                          {formatCurrency(row.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell><SourceChip source={row.source} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{METHOD_LABELS[row.payment_method] || row.provider_channel || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{CHANNEL_LABELS[row.channel] || row.channel}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.external_reference || '-'}</TableCell>
                      <TableCell>{row.term ? `${row.term.name} ${row.term.year}` : '-'}</TableCell>
                      <TableCell><StatusChip status={row.status} /></TableCell>
                      <TableCell>{row.recorded_by || (row.source === 'schoolpay' ? 'SchoolPay' : '-')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            pagination={pagination}
            isMobile={isMobile}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPerPage(size); setPage(1); }}
          />
        </CardContent>
      </Card>

      {/* Details + reconciliation */}
      <Dialog open={Boolean(selected)} onClose={closeDetails} maxWidth="sm" fullWidth fullScreen={isMobile}>
        {selected && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <span>{formatCurrency(selected.amount)}</span>
                <SourceChip source={selected.source} />
                <StatusChip status={selected.status} />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity={selected.status === 'posted' ? 'success' : 'info'} sx={{ mb: 2 }}>
                {STATUS_META[selected.status]?.hint}
              </Alert>
              <Grid container spacing={2}>
                <DetailRow label="Payment ID">{referenceLabel(selected)}</DetailRow>
                <DetailRow label="Payment date">{formatDate(selected.payment_date)}</DetailRow>
                <DetailRow label="Student">{selected.student?.name || '-'}</DetailRow>
                <DetailRow label="Admission no.">{selected.student?.registration_no || '-'}</DetailRow>
                <DetailRow label="Fee group">{selected.fee_group?.name || '-'}</DetailRow>
                <DetailRow label="Term">{selected.term ? `${selected.term.name} ${selected.term.year}` : '-'}</DetailRow>
                <DetailRow label="Payment method">{METHOD_LABELS[selected.payment_method] || '-'}</DetailRow>
                <DetailRow label="Channel">{CHANNEL_LABELS[selected.channel] || selected.channel}</DetailRow>
                <DetailRow label="Recorded by">{selected.recorded_by || (selected.source === 'schoolpay' ? 'SchoolPay integration' : '-')}</DetailRow>
                <DetailRow label="Currency">UGX</DetailRow>
                {selected.source === 'schoolpay' && (
                  <>
                    <DetailRow label="SchoolPay reference">{selected.external_reference}</DetailRow>
                    <DetailRow label="Provider channel">{selected.provider_channel}</DetailRow>
                    <DetailRow label="Name at SchoolPay">{selected.provider_student_name}</DetailRow>
                    <DetailRow label="SchoolPay payment code">{selected.provider_payment_code}</DetailRow>
                    <DetailRow label="SchoolPay status">{selected.provider_status}</DetailRow>
                  </>
                )}
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Remarks</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{selected.remarks || '-'}</Typography>
                </Grid>
              </Grid>

              {selected.record_type === 'schoolpay_transaction' && MATCHABLE_STATUSES.includes(selected.status) && canCreate && (
                <Box mt={3}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Credit this payment to a student</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Posts it to the student's fee ledger once. The payment is applied to the first fee group with an outstanding balance.
                  </Typography>
                  {matchError && <Alert severity="error" sx={{ mb: 1 }}>{matchError}</Alert>}
                  <Autocomplete
                    options={studentOptions}
                    value={matchStudent}
                    inputValue={studentSearch}
                    onInputChange={(e, v) => searchStudents(v)}
                    onChange={(e, v) => setMatchStudent(v)}
                    loading={studentSearchLoading}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    getOptionLabel={(o) => (o ? `${o.registration_no} - ${o.full_name}` : '')}
                    noOptionsText={studentSearch.length < 2 ? 'Type at least 2 characters' : 'No students found'}
                    renderInput={(params) => <TextField {...params} size="small" label="Search student" placeholder="Name or admission number" />}
                  />
                </Box>
              )}

              {selected.record_type === 'schoolpay_adhoc' && selected.status === 'unmatched' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  SchoolPay reported this payment request as paid but it could not be credited automatically. Review it under
                  Fee Management → Online Payments (SchoolPay) → Payment Requests.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selected.student && canViewStudents && (
                <Button onClick={() => navigate(`/students/ledger/${selected.student.id}`)}>Student ledger</Button>
              )}
              <Box flexGrow={1} />
              <Button onClick={closeDetails} disabled={matching}>Close</Button>
              {selected.record_type === 'schoolpay_transaction' && MATCHABLE_STATUSES.includes(selected.status) && canCreate && (
                <Button variant="contained" onClick={handleMatch} disabled={!matchStudent || matching}>
                  {matching ? 'Crediting...' : 'Credit payment'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PaymentsTransactions;
